/**
 * Homepage section order + visibility — single owner for composition.
 */

import type { HomepageLayoutVariant } from "./layoutVariant";

export type HomepageSectionId =
  | "hero"
  | "actionCard"
  | "feed"
  | "recentlyConfirmed"
  | "recentRequests"
  | "recentlySold"
  | "education"
  | "faq"
  | "testimonials";

export type HomepageSectionPlanEntry = {
  id: HomepageSectionId;
  visible: boolean;
};

export type HomepageSectionPlanInput = {
  variant: HomepageLayoutVariant;
  recentItemRequestsEnabled: boolean;
  recentlySoldCount: number;
};

const FULL_ORDER: HomepageSectionId[] = [
  "hero",
  "actionCard",
  "feed",
  "recentlyConfirmed",
  "recentRequests",
  "recentlySold",
  "education",
  "faq",
  "testimonials",
];

/** Compact omits long marketing; reserved for Phase 5. */
const COMPACT_ORDER: HomepageSectionId[] = [
  "hero",
  "actionCard",
  "feed",
  "recentlyConfirmed",
  "recentRequests",
  "recentlySold",
];

function isSectionVisible(
  id: HomepageSectionId,
  input: HomepageSectionPlanInput,
): boolean {
  switch (id) {
    case "recentRequests":
      return input.recentItemRequestsEnabled;
    case "recentlySold":
      // Hide empty box (C4). While loading with zero items, skip skeleton to avoid empty flash.
      return input.recentlySoldCount > 0;
    case "hero":
    case "actionCard":
    case "feed":
    case "recentlyConfirmed":
    case "education":
    case "faq":
    case "testimonials":
      return true;
  }
}

export function getHomepageSectionPlan(
  input: HomepageSectionPlanInput,
): HomepageSectionPlanEntry[] {
  const order = input.variant === "compact" ? COMPACT_ORDER : FULL_ORDER;
  return order.map((id) => ({
    id,
    visible: isSectionVisible(id, input),
  }));
}

export function getVisibleHomepageSections(
  input: HomepageSectionPlanInput,
): HomepageSectionId[] {
  return getHomepageSectionPlan(input)
    .filter((entry) => entry.visible)
    .map((entry) => entry.id);
}
